import { useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { getFastenCatalogSearchCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useFastenSandboxMode from '../useFastenSandboxMode';

const intl = getIntl('useFastenCatalogSearch', {
    errorSearching: 'Error searching for health systems',
});

export type UseFastenCatalogSearchArgs = {
    search: string;
};

export type UseFastenCatalogSearchReturnValue = {
    isLoading: boolean;
    isFetching: boolean;
    entries: APITypes.ehrIntegrationsService.FastenCatalogSearchEntry[];
    /** The Fasten mode the returned entries came from; their ids are only valid for a connect in that mode. */
    entriesApiMode: APITypes.ehrIntegrationsService.FastenApiMode;
};

/**
 * Searches the Fasten Health institution catalog via the backend.
 * The backend proxies Fasten's catalog/search API (Elasticsearch).
 *
 * - `enabled: !!search` — no API call until the user types something.
 * - `staleTime: 1 min` — results are live from Fasten's API, short cache is fine.
 * - `placeholderData: keepPreviousData` — old results stay visible while new search loads.
 *   Use `isFetching` (not `isLoading`) to show a subtle loading indicator (e.g. dim the list).
 */
export default function useFastenCatalogSearch({
    search,
}: UseFastenCatalogSearchArgs): UseFastenCatalogSearchReturnValue {
    const { apiMode, isLoading: isSandboxModeLoading } = useFastenSandboxMode();
    const searchQuery = useQuery({
        queryKey: getFastenCatalogSearchCacheKey(search, apiMode),
        queryFn: async () => {
            const body: APITypes.ehrIntegrationsService.FastenCatalogSearchRequest = {
                search,
                ...(apiMode ? { apiMode } : {}),
            };
            return await appAPI.post(urls.api.ehrIntegrations.fasten.endpoints.catalogSearch, { body });
        },
        enabled: !!search && !isSandboxModeLoading,
        staleTime: 1000 * 60, // 1 min — results are live from Fasten API
        placeholderData: keepPreviousData,
    });
    useQueryErrorNotify(searchQuery, intl('errorSearching'));

    // While keepPreviousData is showing the last mode's results, report the mode those results came from.
    const requestedApiMode = apiMode ?? 'live';
    const entriesApiModeRef = useRef(requestedApiMode);
    useEffect(() => {
        if (!searchQuery.isPlaceholderData) entriesApiModeRef.current = requestedApiMode;
    }, [searchQuery.isPlaceholderData, requestedApiMode]);
    const entriesApiMode = searchQuery.isPlaceholderData ? entriesApiModeRef.current : requestedApiMode;

    return {
        isLoading: searchQuery.isLoading,
        isFetching: searchQuery.isFetching,
        entries: searchQuery.data?.entries || [],
        entriesApiMode,
    };
}
